<script setup lang="ts">
import { computed, inject, ref, watch, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import Check from 'lucide-vue-next/dist/esm/icons/check.js'; import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js';
import { NxAppBrand, NxListRow, NxPopover, NxSearchInput } from '@blpde/nx-ui';
import { accountSelectionKey } from './accountSelection';

defineProps<{ brand: { name: string; account?: string; sub?: string; logo: string | Component } }>();
const selection = inject(accountSelectionKey, null);
const { t } = useI18n({ useScope: 'global' });
const open = ref(false);
const query = ref('');
const trigger = ref<HTMLElement | null>(null);
const results = ref<HTMLElement | null>(null);
const accounts = computed(() => {
  const term = query.value.trim().toLocaleLowerCase();
  return (selection?.accounts.value ?? []).filter((account) =>
    `${account.name} ${account.email}`.toLocaleLowerCase().includes(term),
  );
});
const disabled = computed(() => !selection || selection.pending.value || selection.locked.value);

watch(open, (value) => {
  if (value) query.value = '';
});

function setOpen(value: boolean) {
  open.value = value && !selection?.pending.value;
}
function close() {
  open.value = false;
  trigger.value?.querySelector('button')?.focus();
}
function choose(uuid?: string) {
  if (!uuid || disabled.value) return;
  close();
  if (uuid !== selection?.current.value.uuid) selection?.choose(uuid);
}
function focusFirst() {
  results.value?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
}
</script>

<template>
  <NxPopover v-if="selection" :open="open" side="bottom-start" lazy
    :label="t('connect.accountSharing.switchAccount')" @update:open="setOpen">
    <template #trigger>
      <div ref="trigger" class="account-switcher-trigger">
        <NxAppBrand :brand="brand" class="account-switcher-brand"
          :disabled="selection.pending.value"
          :aria-expanded="open" aria-haspopup="dialog"
          :title="t('connect.accountSharing.switchAccount')" />
        <ChevronDown class="nxt-icon-sm account-switcher-chevron" aria-hidden="true" />
      </div>
    </template>
    <div class="account-switcher-panel" @keydown.esc.stop.prevent="close">
      <NxSearchInput v-model="query" height="s" autofocus
        :placeholder="t('connect.accountSharing.search')"
        :aria-label="t('connect.accountSharing.searchAccounts')"
        @keydown.down.prevent="focusFirst" @keydown.enter.prevent="choose(accounts[0]?.uuid)" />
      <p v-if="selection.locked.value" class="account-switcher-hint" role="status">
        {{ t('connect.accountSharing.closeEditor') }}
      </p>
      <div ref="results" class="account-switcher-results">
        <NxListRow v-for="account in accounts" :key="account.uuid"
          :title="account.name || account.email" :subtitle="account.email"
          :selected="account.uuid === selection.current.value.uuid" :disabled="disabled"
          @open="choose(account.uuid)">
          <template #trailing>
            <Check v-if="account.uuid === selection.current.value.uuid" class="nxt-icon-sm" aria-hidden="true" />
          </template>
        </NxListRow>
        <p v-if="!accounts.length" class="account-switcher-hint" role="status">
          {{ t('connect.accountSharing.noAccounts') }}
        </p>
      </div>
    </div>
  </NxPopover>
  <NxAppBrand v-else :brand="brand" />
</template>

<style scoped>
.account-switcher-trigger { position: relative; }
.account-switcher-brand { padding-inline-end: var(--nxt-space-xl); }
.account-switcher-chevron { position: absolute; inset-inline-end: var(--nxt-space-xs); top: 50%; transform: translateY(-50%); color: var(--nxt-fg-muted); pointer-events: none; }
.account-switcher-panel { width: min(var(--nxt-panel-width-md), 75vw); display: flex; flex-direction: column; gap: var(--nxt-space-xs); }
.account-switcher-results { max-height: min(50vh, var(--nxt-panel-width-lg)); overflow-y: auto; overscroll-behavior: contain; }
.account-switcher-hint { margin: 0; padding: var(--nxt-space-sm); color: var(--nxt-fg-muted); }
</style>
